import React, { useState, useRef, useEffect } from 'react';
import { FaTimes, FaEye, FaEyeSlash } from 'react-icons/fa';
import { BiUser, BiEnvelope, BiLockAlt, BiMap, BiDetail, BiTargetLock } from 'react-icons/bi';
import CustomPhoneInput from '../PhoneInput';
import { toast } from 'react-toastify';

const AddCustomerPopup = ({ onClose, onSuccess }) => {
  const [newCustomer, setNewCustomer] = useState({
    email: '', password: '', name: '', role: 'user', mobileNumber: '', location: '', description: '', callLimit: '', minuteLimitPerCall: '', totalMinuteLimit: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const popupRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (phone) => {
    setNewCustomer(prev => ({ ...prev, mobileNumber: phone }));
  };

  const validateForm = () => {
    if (!newCustomer.name.trim()) {
      toast.error('Name is required');
      return false;
    }
    if (!newCustomer.email.trim()) {
      toast.error('Email is required');
      return false;
    }
    if (!newCustomer.password.trim()) {
      toast.error('Password is required');
      return false;
    }
    if (!newCustomer.mobileNumber.trim()) {
      toast.error('Mobile number is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_API}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newCustomer,
          callLimit: newCustomer.callLimit === '' ? null : Number(newCustomer.callLimit),
          minuteLimitPerCall: newCustomer.minuteLimitPerCall === '' ? null : Number(newCustomer.minuteLimitPerCall),
          totalMinuteLimit: newCustomer.totalMinuteLimit === '' ? null : Number(newCustomer.totalMinuteLimit)
        })
      });
      if (response.ok) {
        const data = await response.json();
        onSuccess(data.user);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to add client');
      }
    } catch (error) {
      console.error('Error adding customer:', error);
      toast.error('Something went wrong!');
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div ref={popupRef} className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        <div className="px-8 py-6 bg-indigo-600 flex justify-between items-center text-white">
          <div>
            <h3 className="text-2xl font-bold">Add New Client</h3>
            <p className="text-indigo-100 text-sm mt-1">Fill in the details to register a new client.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <FaTimes size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Basic Information Section */}
            <div className="space-y-4">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2 mb-2">
                <BiUser /> Identity & Access
              </h4>
              <div className="relative">
                <BiUser className="absolute left-3 top-3.5 text-gray-400" size={18} />
                <input
                  type="text"
                  name="name"
                  value={newCustomer.name}
                  onChange={handleInputChange}
                  placeholder="Full Name"
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white focus:border-indigo-500 transition-all font-medium"
                  required
                />
              </div>
              <div className="relative">
                <BiEnvelope className="absolute left-3 top-3.5 text-gray-400" size={18} />
                <input
                  type="email"
                  name="email"
                  value={newCustomer.email}
                  onChange={handleInputChange}
                  placeholder="Email Address"
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white focus:border-indigo-500 transition-all font-medium"
                  required
                />
              </div>
              <div className="relative">
                <BiLockAlt className="absolute left-3 top-3.5 text-gray-400" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={newCustomer.password}
                  onChange={handleInputChange}
                  placeholder="Password"
                  className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white focus:border-indigo-500 transition-all font-medium"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-indigo-600 transition-colors"
                >
                  {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                </button>
              </div>
            </div>

            {/* Contact & Limits Section */}
            <div className="space-y-4">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2 mb-2">
                <BiTargetLock /> Location & Usage
              </h4>
              <CustomPhoneInput value={newCustomer.mobileNumber} onChange={handlePhoneChange} />
              <div className="relative">
                <BiMap className="absolute left-3 top-3.5 text-gray-400" size={18} />
                <input
                  type="text"
                  name="location"
                  value={newCustomer.location}
                  onChange={handleInputChange}
                  placeholder="Location"
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white focus:border-indigo-500 transition-all font-medium"
                />
              </div>
              <div className="relative">
                <BiTargetLock className="absolute left-3 top-3.5 text-gray-400" size={18} />
                <input
                  type="number"
                  name="callLimit"
                  value={newCustomer.callLimit}
                  onChange={handleInputChange}
                  placeholder="Total Call Limit (Empty for ∞)"
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white focus:border-indigo-500 transition-all font-medium"
                  min="0"
                />
              </div>
              <div className="relative">
                <BiTargetLock className="absolute left-3 top-3.5 text-gray-400" size={18} />
                <input
                  type="number"
                  name="minuteLimitPerCall"
                  value={newCustomer.minuteLimitPerCall}
                  onChange={handleInputChange}
                  placeholder="Minute Limit Per Call"
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white focus:border-indigo-500 transition-all font-medium"
                  min="0"
                />
              </div>
              <div className="relative">
                <BiTargetLock className="absolute left-3 top-3.5 text-gray-400" size={18} />
                <input
                  type="number"
                  name="totalMinuteLimit"
                  value={newCustomer.totalMinuteLimit}
                  onChange={handleInputChange}
                  placeholder="Total Minute Limit"
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white focus:border-indigo-500 transition-all font-medium"
                  min="0"
                />
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2 mb-2">
              <BiDetail /> Additional Details
            </h4>
            <textarea
              name="description"
              value={newCustomer.description}
              onChange={handleInputChange}
              placeholder="Client Description..."
              className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white focus:border-indigo-500 transition-all font-medium min-h-[100px]"
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-200 text-gray-500 font-bold rounded-2xl hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-8 py-3 bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all transform hover:-translate-y-0.5"
            >
              Add Client
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCustomerPopup;
